// GS Mandal — Site Visitor Analytics (super_admin only).
// Shows totals, daily chart, top pages (page name + hits) and top referrers.

import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { Activity, Users, Eye, Globe, BarChart3, Loader2, AlertTriangle } from "lucide-react";

const PERIODS = [
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" },
  { key: "all", label: "All time" },
];

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

export default function Analytics() {
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true); setErr("");
      try {
        const { data } = await api.get("/admin/analytics/overview", { params: { period } });
        setData(data);
      } catch (e) {
        setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load analytics");
      } finally { setLoading(false); }
    })();
  }, [period]);

  const maxDaily = useMemo(() => {
    const s = data?.series || [];
    return Math.max(1, ...s.map((d) => d.views || 0));
  }, [data]);

  const t = data?.totals || { total_views: 0, unique_visitors: 0, avg_pages_per_visitor: 0, tracked_pages: 0 };

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">GS Mandal · Visitor Analytics</div>
          <h1 className="font-serif text-3xl text-[#1c1a17] mt-0.5">Site Traffic</h1>
          <div className="text-sm text-[#6b655b] mt-0.5">Anonymous page views across the public sites (gsmandal.org and institute pages).</div>
        </div>
        <div className="inline-flex rounded-sm border border-[#e5e0d8] overflow-hidden" data-testid="analytics-period-switcher">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setPeriod(p.key)}
              data-testid={`analytics-period-${p.key}`}
              className={`px-3.5 py-2 text-[11px] uppercase tracking-widest font-semibold transition ${
                period === p.key ? "bg-[#234333] text-white" : "bg-white text-[#1c1a17] hover:bg-[#f5f1e8]"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-sm inline-flex items-center gap-2">
          <AlertTriangle size={14} /> {err}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-[#6b655b] flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard icon={Users} color="#0D5C63" label="Unique Visitors" value={fmt(t.unique_visitors)} testid="kpi-visitors" />
            <KpiCard icon={Eye} color="#B8860B" label="Total Page Views" value={fmt(t.total_views)} testid="kpi-views" />
            <KpiCard icon={Activity} color="#234333" label="Pages per Visitor" value={Number(t.avg_pages_per_visitor || 0).toFixed(2)} testid="kpi-ppv" />
            <KpiCard icon={Globe} color="#B22222" label="Unique Pages Hit" value={fmt(t.tracked_pages)} testid="kpi-pages" />
          </div>

          <div className="bg-white border border-[#e5e0d8] rounded-sm p-5" data-testid="analytics-chart">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 size={14} className="text-[#c78e42]" />
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#c78e42]">Daily Views</div>
            </div>
            {(data?.series || []).length === 0 ? (
              <div className="text-xs text-[#8a8478] py-6 text-center">No visits in this period yet.</div>
            ) : (
              <div className="flex items-end gap-1 h-40 pt-2">
                {(data?.series || []).map((d) => {
                  const h = Math.max(2, Math.round((d.views / maxDaily) * 100));
                  return (
                    <div key={d.date} className="flex-1 flex flex-col items-center group min-w-0" data-testid={`analytics-bar-${d.date}`}>
                      <div className="w-full flex items-end" style={{ height: "100%" }}>
                        <div
                          className="w-full bg-gradient-to-t from-[#234333] to-[#0D5C63] group-hover:from-[#F26419] group-hover:to-[#D90368] rounded-t-sm transition"
                          style={{ height: `${h}%` }}
                          title={`${d.date} · ${d.views} views · ${d.unique_visitors} visitors`}
                        />
                      </div>
                      <div className="text-[9px] text-[#8a8478] mt-1 tracking-widest uppercase truncate w-full text-center" title={d.date}>
                        {d.date.slice(5)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Panel title="Top Pages (by hits)" testid="analytics-top-pages">
              {(data?.top_pages || []).length === 0 ? (
                <Empty msg="No pages tracked yet." />
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-[#f5f1e8] text-[10px] tracking-widest uppercase text-[#6b655b]">
                    <tr>
                      <th className="text-left px-4 py-2">Page</th>
                      <th className="text-right px-4 py-2">Hits</th>
                      <th className="text-right px-4 py-2">Visitors</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#f0ece2]">
                    {(data?.top_pages || []).map((p, i) => (
                      <tr key={p.path} data-testid={`analytics-page-row-${i}`}>
                        <td className="px-4 py-2.5">
                          <div className="font-semibold text-[#1c1a17] break-all">{p.title || p.path}</div>
                          <div className="text-[10px] text-[#8a8478] font-mono break-all">{p.path}</div>
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-[#1c1a17]">{fmt(p.views)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-[#6b655b]">{fmt(p.unique_visitors)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>
            <Panel title="Top External Referrers" testid="analytics-top-referrers">
              {(data?.top_referrers || []).length === 0 ? (
                <Empty msg="No external referrers in this period." />
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-[#f5f1e8] text-[10px] tracking-widest uppercase text-[#6b655b]">
                    <tr>
                      <th className="text-left px-4 py-2">Referrer</th>
                      <th className="text-right px-4 py-2">Hits</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#f0ece2]">
                    {(data?.top_referrers || []).map((r, i) => (
                      <tr key={r.referrer} data-testid={`analytics-ref-row-${i}`}>
                        <td className="px-4 py-2.5 text-[#1c1a17] break-all">{r.referrer}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-[#1c1a17]">{fmt(r.views)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, color, label, value, testid }) {
  return (
    <div className="bg-white rounded-sm border border-[#e5e0d8] p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="w-9 h-9 rounded-sm flex items-center justify-center" style={{ background: `${color}18`, color }}>
          <Icon size={16} />
        </div>
      </div>
      <div className="mt-3 font-serif text-3xl text-[#1c1a17]">{value}</div>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mt-0.5">{label}</div>
    </div>
  );
}

function Panel({ title, testid, children }) {
  return (
    <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-hidden" data-testid={testid}>
      <div className="px-5 py-3 border-b border-[#e5e0d8]">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#c78e42] font-bold">{title}</div>
      </div>
      {children}
    </div>
  );
}

function Empty({ msg }) {
  return <div className="text-xs text-[#8a8478] py-6 text-center">{msg}</div>;
}
